/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import Truncate from "@rc/App/Components/Truncate";
import { minicartStyles } from "@rc/App/Components/XngMiniCart/XngMiniCartStyles";
import buildProductDescriptionDataLayerEvent from "@rc/App/Handlers/GoogleAnalytics/ProductDescription/ProductDescriptionClick";
import LazyImage, { LazyImageProps } from "@rc/MobiusOverrides/LazyImage";
import { XngProductContextModel } from "@rc/Overrides/Widgets/ProductList/ProductListProductContext";
import parse from "html-react-parser";
import React from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface MiniCartLineProps {
    setShowSpinner: any;
    cartLine: CartLineModel;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: (id: string, cartLine: CartLineModel) => void;
    cartType?: string;
    shouldDisableQtyInput?: boolean;
    index?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    pageType: getCurrentPage(state).type,
    showTaxAndShipping: getSettingsCollection(state).cartSettings.showTaxAndShipping,
});

const mapDispatchToProps = {
    buildProductDescriptionDataLayerEvent,
};

const XngMiniCartLine: React.FC<
    MiniCartLineProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>
> = ({
    cartLine,
    removeCartLine,
    updateCartLine,
    cartType,
    setShowSpinner,
    shouldDisableQtyInput,
    index,
    pageType,
    showTaxAndShipping,
    buildProductDescriptionDataLayerEvent,
}) => {
    const isInactive = !cartLine.isActive;
    const isRestricted = cartLine.isRestricted;

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    return (
        <StyledWrapper {...minicartStyles.cartLineStyles}>
            <Clickable
                className="remove"
                onClick={() => removeCartLine(cartLine.id, cartLine)}
                {...minicartStyles.cartLineRemoveButtonStyles}
                aria-label="remove line"
            >
                <Icon src="Close" size={12} />
            </Clickable>
            <StyledWrapper {...minicartStyles.cartLineImageWrapperStyles}>
                {!isInactive && !isRestricted ? (
                    <Clickable href={cartLine.productUri} {...minicartStyles.cartLineImageStyles} onClick={hideTippy}>
                        <MiniCartLineImage
                            smallImagePath={cartLine.smallImagePath}
                            altText={cartLine.altText}
                            productUri={cartLine.productUri}
                        />
                    </Clickable>
                ) : (
                    <MiniCartLineImage
                        smallImagePath={cartLine.smallImagePath}
                        altText={cartLine.altText}
                        productUri={cartLine.productUri}
                    />
                )}
            </StyledWrapper>
            <StyledWrapper {...minicartStyles.cartLineDescriptionWrapperStyles} className="description-wrapper">
                <MiniCartLineInformation
                    showTaxAndShipping={showTaxAndShipping}
                    pageType={pageType}
                    setShowSpinner={setShowSpinner}
                    cartLine={cartLine}
                    updateCartLine={updateCartLine}
                    cartType={cartType}
                    shouldDisableQtyInput={shouldDisableQtyInput}
                    index={index}
                    buildProductDescriptionDataLayerEvent={buildProductDescriptionDataLayerEvent}
                    hideTippy={hideTippy}
                />
                {isInactive && (
                    <StyledWrapper className="error-message">
                        <Typography as="span" color="danger.main">
                            {siteMessage("Custom_Mini_Cart_InactiveProduct_Msg")}
                        </Typography>
                    </StyledWrapper>
                )}
                {isRestricted && (
                    <StyledWrapper className="error-message">
                        <Typography as="span" color="danger.main">
                            {siteMessage("Custom_Mini_Cart_RestrictedProduct_Msg")}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

interface MiniCartLineImageProps {
    smallImagePath?: string;
    productUri?: string;
    altText?: string;
}

const MiniCartLineImage = ({ smallImagePath, altText }: MiniCartLineImageProps) => {
    return (
        <LazyImage
            src={smallImagePath}
            altText={altText}
            width="64"
            height="64"
            {...minicartStyles.cartLineImageStyles}
        ></LazyImage>
    );
};

interface MiniCartLineInformationProps {
    setShowSpinner: any;
    cartLine: CartLineModel;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    cartType?: string;
    shouldDisableQtyInput?: boolean;
    showTaxAndShipping: boolean;
    pageType: string;
    index?: number;
    buildProductDescriptionDataLayerEvent?: (parameter: any) => void;
    hideTippy: () => void;
}

const MiniCartLineInformation: React.FC<MiniCartLineInformationProps> = ({
    cartLine,
    updateCartLine,
    cartType,
    setShowSpinner,
    shouldDisableQtyInput,
    showTaxAndShipping,
    pageType,
    index,
    buildProductDescriptionDataLayerEvent,
    hideTippy,
}) => {
    const [stateValue, setValue] = React.useState(cartLine.qtyOrdered?.toString());
    const regexp = /^[0-9\b]+$/;
    const isQuoteRequired = cartLine.quoteRequired;

    const isValidNumberic = (number: string) => {
        if (number === "") {
            // backspace or delete case
            return true;
        }
        return regexp.test(number);
    };

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === 13) {
            event.stopPropagation();
            if (
                event.currentTarget.value !== "" &&
                isValidNumberic(event.currentTarget.value) &&
                parseInt(event.currentTarget.value, 10) <= 999999
            ) {
                (event.target as any).blur();
            }
        }
    };

    React.useEffect(() => {
        setValue(cartLine.qtyOrdered?.toString());
    }, [cartLine.qtyOrdered]);

    const decreaseByOne = () => {
        if (!stateValue || +stateValue === 1) {
            return;
        }
        const newValue = +stateValue - 1;
        onBlurHandler({ target: { value: newValue.toString() } } as any);
    };

    const increaseByOne = () => {
        if (!stateValue || +stateValue === 999999) {
            return;
        }
        const newValue = stateValue ? +stateValue + 1 : 0;
        onBlurHandler({ target: { value: newValue.toString() } } as any);
    };

    const qtyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isValidNumberic(event.currentTarget.value) || parseInt(event.currentTarget.value, 10) > 999999) {
            event.preventDefault();
            return;
        }
        const value = event.target.value;
        if (value.indexOf(".") > -1) {
            return;
        }
        setValue(value);
    };

    const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const stringValue = event.target.value;
        const value = Number.parseInt(stringValue, 10);

        if (!Number.isNaN(value) && value > 0 && value <= 999999 && value !== cartLine.qtyOrdered) {
            setShowSpinner(true);
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered: value,
                    properties: {},
                },
                showTaxAndShipping: pageType.toLowerCase() === "cartpage" && showTaxAndShipping,
                onSuccess: () => {
                    setShowSpinner(false);
                },
            } as any);
        }
        if (value <= 0 || !value || value > 999999) {
            setValue(cartLine.qtyOrdered?.toString());
        }
    };

    const getTotalExtendedActualPrice = () => {
        if (isQuoteRequired) {
            return translate("Quote Required");
        }
        return cartLine?.pricing?.extendedActualPriceDisplay;
    };

    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-wrap: wrap;
                width: 100%;
            `}
        >
            <MiniCartLineDescription
                shortDescription={cartLine.shortDescription}
                productUri={cartLine.productUri}
                isActive={cartLine.isActive}
                isRestricted={cartLine.isRestricted}
                erpNumber={cartLine.erpNumber}
                mfrNumber={cartLine.manufacturerItem}
                weight={cartLine.properties.variant}
                cartLine={cartLine}
                index={index}
                buildProductDescriptionDataLayerEvent={buildProductDescriptionDataLayerEvent}
                hideTippy={hideTippy}
            />
            <StyledWrapper
                css={css`
                    width: 100%;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 12px;
                `}
            >
                <StyledWrapper {...minicartStyles.cartLineOrderedQtyWrapper} className="qty">
                    <span
                        onClick={decreaseByOne}
                        className={`qty-action ${stateValue && +stateValue === 1 ? "disabled" : ""}`}
                    >
                        <Icon src="IconMinus" color="primary" size={10} />
                    </span>
                    <TextField
                        {...minicartStyles.qtyInput}
                        value={stateValue}
                        type="number"
                        min={0}
                        max={999999}
                        label={translate("")}
                        data-test-selector="mini_cart_qtyOrdered"
                        disabled={
                            cartLine.isPromotionItem ||
                            cartType === "Job" ||
                            !cartLine.isActive ||
                            cartLine.isRestricted ||
                            shouldDisableQtyInput
                        }
                        maxLength={6}
                        onKeyPress={keyPressHandler}
                        onChange={qtyChangeHandler}
                        onBlur={onBlurHandler}
                    />
                    <span
                        onClick={increaseByOne}
                        className={`qty-action ${stateValue && +stateValue === 999999 ? "disabled" : ""}`}
                    >
                        <Icon src="IconPlus" color="primary" size={10} />
                    </span>
                </StyledWrapper>

                <StyledWrapper
                    css={css`
                        padding-left: 5px;
                    `}
                >
                    <Typography
                        as="span"
                        size="20px"
                        lineHeight="32px"
                        className="price"
                        color="common.ui6"
                        css={css`
                            font-weight: 500;
                        `}
                    >
                        {getTotalExtendedActualPrice()}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

interface MiniCartLineDescriptionProps {
    productUri?: string;
    shortDescription?: string;
    isRestricted?: boolean;
    isActive?: boolean;
    erpNumber?: string;
    mfrNumber?: string;
    weight?: string;
    cartLine?: CartLineModel;
    index?: number;
    buildProductDescriptionDataLayerEvent?: (parameter: any) => void;
    hideTippy: () => void;
}

const MiniCartLineDescription = ({
    productUri,
    shortDescription,
    isActive,
    isRestricted,
    erpNumber,
    weight,
    cartLine,
    index,
    buildProductDescriptionDataLayerEvent,
    hideTippy,
}: MiniCartLineDescriptionProps) => {
    const description = shortDescription || "";

    const handleClick = (event: React.MouseEvent) => {
        hideTippy();
        buildProductDescriptionDataLayerEvent?.({
            product: cartLine as any,
            productInfo: {} as ProductInfo,
            index,
        } as XngProductContextModel);
    };

    return (
        <StyledWrapper
            css={css`
                display: flex;
                flex-direction: column;
                width: 100%;
            `}
        >
            {isActive && !isRestricted ? (
                <Clickable {...minicartStyles.cartLineDescriptionStyles} href={productUri} onClick={handleClick}>
                    <Typography
                        size="14px"
                        lineHeight="16px"
                        color="common.ui6"
                        css={css`
                            font-weight: 500;
                        `}
                    >
                        <Truncate lines={2} lineHeight={20}>
                            {parse(description)}
                        </Truncate>
                    </Typography>
                </Clickable>
            ) : (
                <Typography
                    size="14px"
                    lineHeight="16px"
                    color="common.ui6"
                    css={css`
                        font-weight: 500;
                    `}
                >
                    <Truncate lines={2} lineHeight={18}>
                        {parse(description)}
                    </Truncate>
                </Typography>
            )}
            {erpNumber && (
                <StyledWrapper>
                    <Typography
                        size="14px"
                        lineHeight="22px"
                        css={css`
                            font-weight: 300;
                        `}
                    >
                        {translate("SKU:")}&nbsp;
                    </Typography>
                    <Typography
                        size="14px"
                        lineHeight="22px"
                        css={css`
                            font-weight: 300;
                        `}
                    >
                        {erpNumber}
                    </Typography>
                </StyledWrapper>
            )}
            {weight && (
                <StyledWrapper>
                    <Typography
                        size="14px"
                        lineHeight="22px"
                        css={css`
                            font-weight: 300;
                        `}
                    >
                        {translate("Weight:")}&nbsp;
                    </Typography>
                    <Typography
                        size="14px"
                        lineHeight="22px"
                        css={css`
                            font-weight: 300;
                        `}
                    >
                        {weight}
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(XngMiniCartLine);
